/**
 * A service's favicon (#11635): the mark beside a portal service's name on
 * the Connectors page and in Documents, fetched by the browser from a public
 * favicon service keyed by the service's host. The API emits the host, never
 * the image. When the host is unknown, or no source has a favicon for it,
 * the caller's fallback mark renders instead, so a row is never left with a
 * broken image.
 *
 * Resolution (slice 1, R8): Google's favicon service for the host, then,
 * only for a subdomain, for its registrable domain
 * (`investors.example-portal.com` → `example-portal.com`), then the
 * fallback. Google answers a miss with a 404 whose body is its 16px
 * placeholder, so the image fires `load`, not `error`: a source is passed
 * over when what loads is narrower than 32px (64 is asked for), or when it
 * errors. A real icon narrower than 32px (some services serve a 16px one)
 * also reads as a miss and falls to the next source: an accepted
 * trade-off. One image at a time: the next source is asked only once the
 * current one has missed.
 */
import React, { useState } from "react";

const GOOGLE = "https://www.google.com/s2/favicons";

/** The size asked of the service, in pixels. */
const REQUESTED_PX = 64;

/** Narrower than this, a loaded image is Google's placeholder (16px when
 *  64 is asked for), not the service's icon. */
const PLACEHOLDER_BELOW_PX = 32;

/** The Google favicon URL for a host. */
const googleFaviconUrl = (host: string): string =>
  `${GOOGLE}?domain=${encodeURIComponent(host)}&sz=${REQUESTED_PX}`;

// Second-level labels that, under a two-letter country code, are part of
// the public suffix ("co.uk", "com.au"): the registrable domain there keeps
// three labels.
const SECOND_LEVEL_SUFFIXES = new Set([
  "co",
  "com",
  "net",
  "org",
  "gov",
  "edu",
  "ac",
  "ltd",
  "plc",
]);

/** The host's registrable domain: its last two labels, or three under a
 *  country code's second-level suffix. The host itself when it is one. */
export const registrableDomain = (host: string): string => {
  const labels = host.toLowerCase().replace(/\.$/, "").split(".");
  if (labels.length <= 2) return labels.join(".");
  const tld = labels[labels.length - 1] ?? "";
  const second = labels[labels.length - 2] ?? "";
  const keep = tld.length === 2 && SECOND_LEVEL_SUFFIXES.has(second) ? 3 : 2;
  return labels.slice(-keep).join(".");
};

/** The sources in the order they are tried. */
export const faviconSources = (host: string): string[] => {
  const domain = registrableDomain(host);
  const sources = [googleFaviconUrl(host)];
  if (domain !== host.toLowerCase()) sources.push(googleFaviconUrl(domain));
  return sources;
};

export interface ServiceFaviconProps {
  /** The service's host ("junipersquare.com"), or null when unknown. */
  host: string | null;
  /** The service's name, for the image's accessible name. */
  label: string;
  size?: number;
  /** What renders when there is no host or no source has a favicon. */
  fallback: React.ReactNode;
  className?: string;
}

export const ServiceFavicon: React.FC<ServiceFaviconProps> = ({
  host,
  label,
  size = 16,
  fallback,
  className,
}) => {
  // How many sources have missed, for the host they missed for: a new host
  // is a new walk, and the old host's misses must not skip its sources.
  const [missed, setMissed] = useState<{ host: string | null; count: number }>({
    host,
    count: 0,
  });
  const attempt = missed.host === host ? missed.count : 0;
  if (host === null || host === "") return <>{fallback}</>;
  const sources = faviconSources(host);
  const src = sources[attempt];
  if (src === undefined) return <>{fallback}</>;
  const next = () => setMissed({ host, count: attempt + 1 });
  return (
    <img
      // A new element per source, so one source's load is never read as
      // another's.
      key={src}
      src={src}
      alt={label}
      width={size}
      height={size}
      className={`shrink-0 rounded-[3px] ${className ?? ""}`}
      onLoad={(event) => {
        if (event.currentTarget.naturalWidth < PLACEHOLDER_BELOW_PX) next();
      }}
      onError={next}
    />
  );
};
